import { isLiteraryApp } from '@models/AppType'
import { AppStatus, RequestState } from '@models/Common'
import { PredictionResults, PredictionData, DsmpPrediction, Prediction } from '@models/Prediction/Prediction'
import { Action } from '@utils/reducers/Action'
import environment from 'config/environment'
import { logReducerAction } from '../dev/devSessionLog'
import { isDsmpPrediction, dsmpScoreFromPrediction } from '@utils/categoryFlow'

export type PredictionState = RequestState & {
  citizenForecasting?: PredictionData
  prediction?: PredictionData
  predictionVeracity?: boolean
  rectifiedPrediction?: PredictionData
  results?: PredictionResults
  sandboxedResults?: PredictionResults
  sandboxedPrediction?: PredictionData
  voteProbability?: number
  dsmpQ1?: number
  dsmpQ2?: number
}

export const initialState: PredictionState = {
  status: AppStatus.IDLE,
  errorMessage: undefined,
  results: undefined,
}

export enum PredictionActionType {
  FetchPredictionFailure = 'FetchPredictionFailure',
  FetchPredictionPending = 'FetchPredictionPending',
  FetchPredictionSuccess = 'FetchPredictionSuccess',
  FetchSandboxedPredictionFailure = 'FetchSandboxedPredictionFailure',
  FetchSandboxedPredictionPending = 'FetchSandboxedPredictionPending',
  FetchSandboxedPredictionSuccess = 'FetchSandboxedPredictionSuccess',
  SetCitizenForecasting = 'SetCitizenForecasting',
  SetRectifiedPrediction = 'SetRectifiedPrediction',
  SetVeracity = 'SetVeracity',
  SetVoteProbability = 'SetVoteProbability',
  SubmitForecastingFailure = 'SubmitForecastingFailure',
  SubmitForecastingSuccess = 'SubmitForecastingSuccess',
  SubmitRectificationFailure = 'SubmitRectificationFailure',
  SubmitRectificationPending = 'SubmitRectificationPending',
  SubmitRectificationSuccess = 'SubmitRectificationSuccess',
  SetState = 'SetState',
}

const getTarget = (results: PredictionResults) => {
  const prediction = results.prediction as Prediction
  const data = prediction?.data
  if (!data?.length) {
    throw new Error('Prediction results missing data array')
  }
  const { selected } = prediction
  const target = data.find(({ id }) => selected === id)
  if (target) {
    return target
  }
  // Fallback to highest value
  return data.reduce((prev, current) => (prev.value > current.value ? prev : current))
}

const setResults = (state: PredictionState, results: PredictionResults): PredictionState => {
  if (isDsmpPrediction(results.prediction)) {
    const score = dsmpScoreFromPrediction(results.prediction)!
    const phase = (results.prediction as DsmpPrediction).phase
    return {
      ...state,
      results,
      dsmpQ1: phase === 'q1' ? score : state.dsmpQ1,
      dsmpQ2: phase === 'q2' ? score : state.dsmpQ2,
      status: AppStatus.IDLE,
    }
  }

  const electoral = results.prediction as Prediction
  if (!electoral?.data?.length) {
    return { ...state, results, status: AppStatus.IDLE }
  }

  if (isLiteraryApp) {
    electoral.data.sort((a, b) => b.value - a.value)
  }

  const target = getTarget(results)

  return {
    ...state,
    results,
    prediction: target,
    status: AppStatus.IDLE,
  }
}

const setSandboxedResults = (state: PredictionState, sandboxedResults: PredictionResults): PredictionState => {
  if (isDsmpPrediction(sandboxedResults.prediction)) {
    return setResults(state, sandboxedResults)
  }

  const electoral = sandboxedResults.prediction as Prediction
  if (!electoral?.data?.length) {
    return { ...state, sandboxedResults, status: AppStatus.IDLE }
  }
  if (isLiteraryApp) {
    electoral.data.sort((a, b) => b.value - a.value)
  }

  const target = getTarget({ ...sandboxedResults, prediction: electoral })

  return {
    ...state,
    sandboxedResults,
    sandboxedPrediction: target,
    status: AppStatus.IDLE,
  }
}

const setRectifiedPrediction = (state: PredictionState, rectifiedPrediction: PredictionData): PredictionState => ({
  ...state,
  rectifiedPrediction,
})

const setCitizenForecasting = (state: PredictionState, citizenForecasting: PredictionData): PredictionState => ({
  ...state,
  citizenForecasting,
})

const setPredictionVeracity = (state: PredictionState, predictionVeracity: boolean): PredictionState => ({
  ...state,
  predictionVeracity,
})

const PredictionReducer = (state: PredictionState, action: Action<PredictionActionType>): PredictionState => {
  const { payload, type } = action

  if (environment.isDev) {
    // eslint-disable-next-line no-console
    console.log({ reducer: 'prediction', state, action })
    logReducerAction('prediction', action)
  }

  switch (type) {
    case PredictionActionType.FetchPredictionPending:
      return { ...state, status: AppStatus.PENDING }

    case PredictionActionType.FetchPredictionSuccess:
      return setResults(state, payload as PredictionResults)

    case PredictionActionType.FetchPredictionFailure:
      return { ...state, status: AppStatus.ERROR, errorMessage: payload as string }

    case PredictionActionType.SubmitRectificationPending:
      return { ...state, status: AppStatus.PENDING }

    case PredictionActionType.SubmitRectificationSuccess:
      return { ...state, status: AppStatus.IDLE }

    case PredictionActionType.SubmitForecastingSuccess:
      return { ...state, status: AppStatus.PENDING }

    case PredictionActionType.SubmitForecastingFailure:
      return { ...state, status: AppStatus.ERROR, errorMessage: payload as string }

    case PredictionActionType.SubmitRectificationFailure:
      return { ...state, status: AppStatus.ERROR, errorMessage: payload as string }

    case PredictionActionType.SetVeracity:
      return setPredictionVeracity(state, payload as boolean)

    case PredictionActionType.SetCitizenForecasting:
      return setCitizenForecasting(state, payload as PredictionData)

    case PredictionActionType.SetRectifiedPrediction:
      return setRectifiedPrediction(state, payload as PredictionData)

    case PredictionActionType.FetchSandboxedPredictionPending:
      return { ...state, status: AppStatus.PENDING }

    case PredictionActionType.FetchSandboxedPredictionSuccess:
      return setSandboxedResults(state, payload as PredictionResults)

    case PredictionActionType.FetchSandboxedPredictionFailure:
      return { ...state, status: AppStatus.ERROR, errorMessage: payload as string }

    case PredictionActionType.SetVoteProbability:
      return { ...state, voteProbability: payload as number }

    case PredictionActionType.SetState:
      return { ...state, ...(payload as PredictionState) }

    default:
      return state
  }
}

export default PredictionReducer
